import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { getSession, useSession } from 'next-auth/react';
import { useState } from 'react';

import { AuthenticationModal } from '../../../../components/_molecules/AuthenticationModal/AuthenticationModal';
import DesignerSkeletonLoader from '../../../../components/_molecules/DesignerSkeletonLoader';
import { DesignerTopBarContainer } from '../../../../components/styled/CreatorTopBar';
import ActiveBackgroundProvider from '../../../../providers/ActiveBackgroundProvider';
import { PremiumTestPrepPlaywallProvider } from '../../../../providers/PremiumTestPrepPlaywallProvider/PremiumTestPrepPlaywallProvider';
import { getErroredSessionSsrRedirect } from '../../../../utils/erroredSessionRedirectSsr';
import { detectAndBestEffortSetLanguage } from '../../../../utils/localisationUtils';

const FullPageCreatorLoader = () => {
  return (
    <>
      <DesignerTopBarContainer />
      <DesignerSkeletonLoader />
    </>
  );
};

const DesignerPage = dynamic(
  () => import('../../../../components/dynamicPages/DesignerPage'),
  { ssr: false, loading: () => <FullPageCreatorLoader /> },
);

const CreatorRootPage = () => {
  const router = useRouter();
  const session = useSession();
  const isAuthenticated = session.status === 'authenticated';

  // Get templateId from URL
  const templateId = router.query.templateId as string;

  const [showAuthModal, _] = useState(!isAuthenticated);

  const handleSignIn = () => {
    const currentPath = router.asPath;
    const signinUrl = `/login?redirect=${encodeURIComponent(currentPath)}`;
    router.push(signinUrl);
  };
  const handleSignUp = () => {
    const currentPath = router.asPath;
    const signupUrl = `/signup?callbackUrl=${encodeURIComponent(currentPath)}`;
    router.push(signupUrl);
  };
  return (
    <ActiveBackgroundProvider>
      <PremiumTestPrepPlaywallProvider>
        {isAuthenticated ? (
          <DesignerPage />
        ) : session.status === 'loading' ? (
          <FullPageCreatorLoader />
        ) : (
          <>
            <FullPageCreatorLoader />
            <AuthenticationModal
              open={showAuthModal}
              onSignIn={handleSignIn}
              onSignUp={handleSignUp}
              templateId={templateId}
            />
          </>
        )}
      </PremiumTestPrepPlaywallProvider>
    </ActiveBackgroundProvider>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  // Hand the server-verified session to the SessionProvider (via _app) so the
  // editor's client-side auth check resolves to `authenticated` on first render
  // instead of racing a /api/auth/session fetch. When that fetch lost the race it
  // resolved `unauthenticated` and NextAuth cached it (no auto-retry), wrongly
  // showing the "please log in" modal to a freshly-logged-in user landing here
  // (e.g. after a forced-logout redirect back to the editor). Every other app page
  // returns session in props for the same reason. Left null → the modal still
  // renders for genuinely logged-out visitors (e.g. a shared edit link).
  const session = await getSession({ req: context.req });
  // An errored session (ProvisioningError / RefreshAccessTokenError / no token) is
  // still truthy and would be hydrated below, tripping the BackendServiceProvider
  // recovery into a client-side login loop. Clear it server-side and redirect out.
  // A genuinely logged-out visitor (null session) falls through and still renders
  // the editor (with the "please log in" modal) — e.g. a shared edit link.
  const erroredRedirect = getErroredSessionSsrRedirect(session, context);
  if (erroredRedirect) return erroredRedirect;
  return {
    props: {
      ...(session && { session }),
      ...(await detectAndBestEffortSetLanguage(context, { routeClass: 'app' })),
    },
  };
};

export default CreatorRootPage;
